import { SubscriptionPaymentType } from '@thg-commerce/enterprise-components/ProductVariations'

import {
  AvailabilitySource,
  FulfilmentMethod,
  OptionChoice,
  ProductPageQuery,
  SubscriptionContract,
  SubscriptionFrequency,
} from '../../../generated/graphql'

export interface Rrp {
  displayValue: string
  __typename?: 'RRP' | string
}
export interface Images {
  largeProduct: string
  __typename?: 'Images' | string
}
export interface LandingPageLink {
  text: string
  url: string
  openExternally: boolean
  noFollow: boolean
  __typename?: 'LandingPageLink' | string
}
export interface FreeGiftProduct {
  images: Images[]
  title: string
  price: {
    rrp: Rrp
    __typename?: 'Price' | string
  }
  __typename?: 'FreeGiftProduct' | string
}
export interface MarketedSpecialOfferSummary {
  title: string
  description: string
  landingPageLink?: LandingPageLink
  freeGiftProduct?: FreeGiftProduct | null
  __typename?: 'ProductMarketedSpecialOffer' | string
}
export type Description = {
  items: {
    key: string
    titleKey: string
    content: string
    __typename: 'ProductDescriptionItem'
  }[]
  details: {
    key: string
    titleKey: string
    value: string
    __typename: 'ProductDescriptionDetail'
  }[]
  provenance: {
    enabled: boolean
    textItem: string | null
    __typename: 'ProductDescriptionProvenance'
  }
  videoTitle: string | null
  videoReference: string | null
  deliveryTitle: string | null
  __typename: 'ProductDescription'
} | null

export interface PrefetchTagAttributes {
  href: string
  as: string
  rel: 'prefetch' | 'preload'
  fetchpriority: 'high' | 'low' | 'auto'
  __typename: 'ProductPrefetchImage'
}

export interface ProductGallery {
  thumbnails: { url: string; alt: string; __typename: 'ProductGalleryImage' }[]
  images: {
    url: string
    alt: string
    tag?: string
    __typename: 'ProductGalleryImage'
  }[]
  zoom: {
    url: string
    alt: string
    tag?: string
    __typename: 'ProductGalleryImage'
  }[]
  prefetches: {
    zoom: PrefetchTagAttributes[]
    images: PrefetchTagAttributes[]
  }
  __typename: 'ProductGallery'
}

export interface QubitProductEvent {
  images: string[]
  categories: string[]
  stock: number
  productId: string
  sku: string
  name: string
  url: string
  price: { currency: string; value: number }
  originalPrice: { currency: string; value: number }
}

type RequiredProduct = NonNullable<Required<ProductPageQuery['product']>>

export type ProductVariant = NonNullable<RequiredProduct['variants']>[0]
export type ProductVariantWithDescription = Omit<
  ProductVariant,
  'product' | 'subscriptionPaymentType'
> & {
  subscriptionPaymentType?: SubscriptionPaymentType
  product?:
    | (Omit<ProductVariant['product'], 'content'> & {
        description?: Description
        preorder?: boolean
        preorderReleaseDate?: string
      })
    | null
  gallery: ProductGallery | null
  subtitle: string | null
  masterSkuTitle?: string
  reviews: { total: number; averageScore: number } | null
  url: string | null
  breadcrumbs: { url: string; text: string }[] | null
  breadcrumbSchemaItems:
    | {
        '@type': 'ListItem' | string
        position: number
        name: string
        item: string
      }[]
    | null
  availabilitySource?: AvailabilitySource
  deliveryInformation?: string
  returnsInformation?: string
  deliveryReturnsContentDefined?: boolean
  subscriptionData: SubscriptionData
  subscriptionContracts?: SubscriptionContract[]
  eligibleForFulfilmentMethods?: FulfilmentMethod[]
  isBookable?: Boolean
  isCheckStock: Boolean
  isOrderInStore: Boolean
  leadTime?: number
  marketedSpecialOffer?: MarketedSpecialOfferSummary
  personalisationFields?: [
    | MultiSelectionProductPersonalisationField
    | FreeTextProductPersonalisationField
    | SingleSelectionProductPersonalisationField,
  ]
  personalisationData?: PersonalisationData
  priceRange?: string
  rrpRange?: string
}

export type FreeTextProductPersonalisationField = {
  name: string
  title: string
  type: string
  required: boolean
  maxLength: number
  __typename: 'FreeTextProductPersonalisationField'
}

export type SingleSelectionProductPersonalisationField = {
  name: string
  title: string
  type: string
  required: boolean
  options: {
    name: string
    value: string
    displayAsset: {
      url: string
      alt: string
    }
    previewAssetSetIdentifier: string
    order: number
  }[]
  choices: Choice[]
  __typename: 'SingleSelectionProductPersonalisationField'
}

export type MultiSelectionProductPersonalisationField = {
  name: string
  title: string
  type: string
  required: boolean
  options: {
    name: string
    value: string
    displayAsset: {
      url: string
      alt: string
    }
    previewAssetSetIdentifier: string
    order: number
  }[]
  choices: Choice[]
  __typename: 'MultiSelectionProductPersonalisationField'
}

export type PersonalisationData = {
  personalisationFonts?: PersonalisationFontsObject[]
  personalisationSupportImages?: PersonalisationGallery[]
}
export type PersonalisationFontsObject = {
  name: string
  family: string
  weight: number
  lineHeight: number
  letterSpacing: number
  maxPreviewFontSize: number
}
export type PersonalisationImage = { url: string; alt: string }

export type PersonalisationGallery = {
  key: string
  thumbnails: PersonalisationImage[]
  images: PersonalisationImage[]
  zoom: PersonalisationImage[]
}

export const HOME_URL = '/'

export type SubscriptionChoice = OptionChoice & {
  price?: string
  saving: string | null
}

export type SubscriptionData = {
  isSubscription: boolean
  isAutoRenewSubscription: boolean
  subscriptionFrequency: SubscriptionFrequency | null
  subscriptionTerm: number | null
  subscriptionPaymentType: SubscriptionPaymentType | null
  subscriptionContracts: SubscriptionContract[]
  choices: SubscriptionChoice[]
}

export interface Choice {
  image?: string
  key: string
  colour: string
  title: string
  disabled?: boolean
}
